/**
 * The consent decision an embedding site relays to the checkout.
 *
 * Embedded on a venue's own domain the checkout never renders its own banner, so
 * the host site's decision is the only one that exists. The legacy booking
 * widget already forwards it: with `enable-gtm-consent` set,
 * `WindowManager.getCookieConsentEvents()` filters the host page's
 * `window.dataLayer` for the four `cookie_consent_*` events and appends them to
 * the checkout URL as a JSON `gtm_consent` parameter.
 *
 * The parameter is third-party input on a URL a guest can edit, so it is treated
 * as untrusted: bounded in length, parsed defensively and reduced to our own
 * {@link ConsentCategories} rather than replayed into `window.dataLayer` the way
 * the legacy checkout does. Anything unrecognised grants nothing.
 *
 * A second shape is also read here: the timestamped
 * {@link CONSENT_RELAY_PARAM} token the checkout itself relays across a domain
 * boundary. Because it carries the moment the decision was taken, it is judged
 * against four safeguards — freshness, clock sanity, newest-wins and fail-safe —
 * and ignored entirely when it fails any of them. An ignored relay never grants
 * anything and never displaces a decision recorded locally. See
 * `docs/consent.md`.
 *
 * Inline on a venue's own page the same `cookie_consent_*` events are readable
 * directly from the host's own `window.dataLayer`, with no relay needed: that path
 * lives in `hostDataLayer.ts` and reduces the entries it observes with the
 * recognition and category mapping below, so a decision means the same thing
 * however it reached us.
 */
import { type ConsentCategories, NO_CONSENT_CATEGORIES } from "../services/cookieConsent/types";
import { readLocalConsentDecisionTime } from "./consentDecisionRecord";
import {
  CONSENT_RELAY_PARAM,
  type ConsentRelayToken,
  decodeConsentRelayToken,
  type RelayedSignals,
} from "./consentRelay";

/** URL parameter carrying the host site's relayed consent events. */
export const HOST_CONSENT_PARAM = "gtm_consent";

/** A relayed decision older than this is stale and is ignored. */
export const RELAY_FRESHNESS_WINDOW_MS = 24 * 60 * 60 * 1000;

/**
 * How far ahead of our own clock a relayed decision may be dated.
 *
 * Client clocks disagree by seconds to minutes in practice, so a small
 * allowance avoids discarding honest relays; beyond it the timestamp cannot be
 * trusted, and a far-future one would otherwise defeat the freshness window.
 */
export const RELAY_CLOCK_SKEW_ALLOWANCE_MS = 5 * 60 * 1000;

/** Which relay shape a URL carried. */
export type HostConsentRelayShape = "none" | "legacy-events" | "signal-token";

/** What was done with the relayed decision, and why. */
export type HostConsentRelayOutcome =
  | "none"
  | "applied"
  | "ignored-malformed"
  | "ignored-stale"
  | "ignored-future"
  | "ignored-superseded";

/**
 * A relayed decision and its fate, in a form safe to log: purposes and signal
 * states only, never the raw token and nothing identifying.
 */
export interface HostConsentRelay {
  readonly shape: HostConsentRelayShape;
  readonly outcome: HostConsentRelayOutcome;
  /** The decision to act on, or `null` when there is none to act on. */
  readonly categories: ConsentCategories | null;
  /** Token version, where the relay carried one. */
  readonly version: number | null;
  /** How old the relayed decision was, in whole seconds, where it was dated. */
  readonly ageSeconds: number | null;
  /** The consent mode signals relayed, where the relay carried them. */
  readonly signals: RelayedSignals | null;
}

const NO_RELAY: HostConsentRelay = {
  shape: "none",
  outcome: "none",
  categories: null,
  version: null,
  ageSeconds: null,
  signals: null,
};

/**
 * Longest `gtm_consent` value parsed. A genuine relay carries at most a handful
 * of small objects; anything larger is discarded rather than parsed.
 */
const MAX_HOST_CONSENT_LENGTH = 4096;

/** The purposes a host site can speak about. `essential` is never in question. */
type RelayableCategory = "analytics" | "performance" | "advertising";

/**
 * Recognised relayed event names, and the purpose each one is about.
 *
 * The names come from the widget's filter list. `cookie_consent_update` is the
 * summary event and may carry any combination of purposes, so it maps to no
 * single one; the other three are per-purpose events, mapping onto our category
 * names in the same way Termly's own categories do: statistics is measurement,
 * marketing is advertising, and preferences is the functional/personalisation
 * storage our `performance` category covers.
 */
const RELAYED_EVENT_CATEGORIES: Record<string, RelayableCategory | null> = {
  cookie_consent_update: null,
  cookie_consent_statistics: "analytics",
  cookie_consent_marketing: "advertising",
  cookie_consent_preferences: "performance",
};

/**
 * Keys a relayed event may state a purpose with, in either Google consent mode
 * v2 spelling or the host platform's own category naming.
 */
const RELAYED_STATE_KEYS: Record<string, RelayableCategory> = {
  analytics_storage: "analytics",
  analytics: "analytics",
  statistics: "analytics",
  ad_storage: "advertising",
  ad_user_data: "advertising",
  ad_personalization: "advertising",
  advertising: "advertising",
  marketing: "advertising",
  functionality_storage: "performance",
  personalization_storage: "performance",
  performance: "performance",
  preferences: "performance",
};

/** Values read as a grant. Everything else, including absent, is a refusal. */
const GRANTED_VALUES = new Set(["granted", "true", "accepted", "yes", "1"]);

function isGranted(value: unknown): boolean {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") return GRANTED_VALUES.has(value.trim().toLowerCase());

  return false;
}

/** Own properties only: an inherited name is not something a host stated. */
function states(entry: object, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(entry, key);
}

/** Whether a relayed entry states anything at all about a purpose. */
function statesCategory(entry: Record<string, unknown>, category: RelayableCategory): boolean {
  return Object.entries(RELAYED_STATE_KEYS).some(([key, stated]) => stated === category && states(entry, key));
}

/**
 * Applies one relayed event to the purposes gathered so far.
 *
 * A per-purpose event that states nothing explicit is itself the statement: the
 * host pushed it because the visitor accepted that purpose. An event that does
 * state a purpose is honoured as stated, so a relayed refusal stays a refusal.
 */
function applyRelayedEvent(
  granted: Partial<Record<RelayableCategory, boolean>>,
  entry: Record<string, unknown>,
  eventCategory: RelayableCategory | null,
): void {
  for (const [key, category] of Object.entries(RELAYED_STATE_KEYS)) {
    if (states(entry, key)) granted[category] = isGranted(entry[key]);
  }

  if (eventCategory && !statesCategory(entry, eventCategory)) granted[eventCategory] = true;
}

/**
 * Whether an entry is one of the four host consent events.
 *
 * Exported for the inline path, which applies it to a host's own `dataLayer`
 * entries — everything else on a host's dataLayer is theirs and is ignored.
 */
export function isHostConsentEvent(value: unknown): value is Record<string, unknown> & { event: string } {
  if (typeof value !== "object" || value === null) return false;

  const event = (value as Record<string, unknown>).event;

  // `toString` and friends are inherited, not one of the four relayed events.
  return typeof event === "string" && states(RELAYED_EVENT_CATEGORIES, event);
}

function parseRelayedEvents(raw: string): Record<string, unknown>[] | null {
  if (raw.length > MAX_HOST_CONSENT_LENGTH) return null;

  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;

    const events = parsed.filter(isHostConsentEvent);

    return events.length > 0 ? events : null;
  } catch {
    return null;
  }
}

/** Last query string read, and what it produced, so callers can compare by identity. */
let lastRead: { readonly search: string; readonly relay: HostConsentRelay } | null = null;

/**
 * Reads the decision an embedding site relayed on the URL.
 *
 * The same query string always yields the same object, so a React effect keyed
 * on the result does not re-run on every render.
 *
 * @param search query string to read; defaults to the current page's.
 * @returns the relayed decision, or `null` when the host relayed no
 * recognisable decision — which the checkout treats exactly as it treats a
 * refusal, emitting nothing at all.
 */
export function readHostConsentCategories(search: string = window.location.search): ConsentCategories | null {
  return readHostConsentRelay(search).categories;
}

/**
 * Reads the relayed decision and what was done with it.
 *
 * The same query string always yields the same object, so a caller may compare
 * by identity and report the outcome once rather than on every render.
 *
 * @param search query string to read; defaults to the current page's.
 */
export function readHostConsentRelay(search: string = window.location.search): HostConsentRelay {
  if (lastRead?.search === search) return lastRead.relay;

  const relay = parseHostConsentRelay(search);
  lastRead = { search, relay };

  return relay;
}

function parseHostConsentRelay(search: string): HostConsentRelay {
  const params = new URLSearchParams(search);

  // The timestamped token is the richer shape and the only one that can be
  // judged for freshness, so it governs wherever it is present.
  const token = params.get(CONSENT_RELAY_PARAM);
  if (token) return evaluateRelayedToken(token);

  const raw = params.get(HOST_CONSENT_PARAM);
  if (!raw) return NO_RELAY;

  return evaluateRelayedEvents(raw);
}

/**
 * Judges a timestamped token against the four safeguards.
 *
 * Order matters only in what it reports: a token failing more than one
 * safeguard is ignored either way, and the first failure found is the one
 * recorded.
 */
function evaluateRelayedToken(raw: string, now: number = Date.now()): HostConsentRelay {
  const token = decodeConsentRelayToken(raw);
  if (!token) return { ...NO_RELAY, shape: "signal-token", outcome: "ignored-malformed" };

  const relayed: HostConsentRelay = {
    shape: "signal-token",
    outcome: "ignored-malformed",
    categories: null,
    version: token.version,
    ageSeconds: Math.trunc((now - token.decidedAt) / 1000),
    signals: token.signals,
  };

  if (token.decidedAt > now + RELAY_CLOCK_SKEW_ALLOWANCE_MS) return { ...relayed, outcome: "ignored-future" };
  if (now - token.decidedAt > RELAY_FRESHNESS_WINDOW_MS) return { ...relayed, outcome: "ignored-stale" };

  const localDecidedAt = readLocalConsentDecisionTime();
  if (localDecidedAt !== null && token.decidedAt <= localDecidedAt) {
    return { ...relayed, outcome: "ignored-superseded" };
  }

  return { ...relayed, outcome: "applied", categories: toRelayedCategories(token) };
}

/**
 * The purposes a token speaks about.
 *
 * It carries the four consent mode v2 signals and nothing else, so
 * `performance` — functionality and personalisation storage — is not something
 * it can grant, and stays refused.
 */
function toRelayedCategories(token: ConsentRelayToken): ConsentCategories {
  return {
    ...NO_CONSENT_CATEGORIES,
    analytics: token.signals.analytics_storage === "granted",
    advertising: token.signals.ad_storage === "granted",
  };
}

/**
 * Reads the legacy, untimestamped relay.
 *
 * It cannot be judged for freshness, so it is honoured exactly as it always has
 * been: the widget builds it from the host page's own `dataLayer` as it hands
 * the guest over, so it is the host's decision as at that moment, and on a host
 * we do not own it is the only decision that exists. See `docs/consent.md`.
 */
function evaluateRelayedEvents(raw: string): HostConsentRelay {
  const events = parseRelayedEvents(raw);
  const categories = events ? toHostConsentCategories(events) : null;

  if (!categories) return { ...NO_RELAY, shape: "legacy-events", outcome: "ignored-malformed" };

  return { ...NO_RELAY, shape: "legacy-events", outcome: "applied", categories };
}

/**
 * Reduces host consent events, in the order the host stated them, to the
 * purposes they grant.
 *
 * Later events win, so a mid-session change is respected wherever the events came
 * from. Nothing recognisable is nothing to act on: the caller then behaves exactly
 * as it does for a refusal.
 *
 * @param events entries already recognised by {@link isHostConsentEvent}.
 * @returns the decision, or `null` when there is none.
 */
export function toHostConsentCategories(events: readonly Record<string, unknown>[]): ConsentCategories | null {
  if (events.length === 0) return null;

  const granted: Partial<Record<RelayableCategory, boolean>> = {};
  for (const entry of events) {
    applyRelayedEvent(granted, entry, RELAYED_EVENT_CATEGORIES[entry.event as string]);
  }

  return {
    ...NO_CONSENT_CATEGORIES,
    analytics: granted.analytics === true,
    performance: granted.performance === true,
    advertising: granted.advertising === true,
  };
}
